/* ============================================================
   Wido Ops — chart primitives. On-brand, hand-built SVG/CSS.
   Exports to window: BarChart, DonutGauge, LinearMeter,
   SegmentMeter, Sparkline, StackBars.
   ============================================================ */
(function () {
  const { useState, useEffect, useRef } = React;

  // Animate a number 0→value once mounted (respects reduced motion).
  function useGrow(active) {
    const [g, setG] = useState(active ? 0 : 1);
    useEffect(() => {
      if (!active) { setG(1); return; }
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce) { setG(1); return; }
      let raf, start;
      const dur = 700;
      const tick = (t) => {
        if (!start) start = t;
        const p = Math.min(1, (t - start) / dur);
        setG(1 - Math.pow(1 - p, 3));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, [active]);
    return g;
  }

  // ---- Bar chart: late minutes per day ----
  function BarChart({ data, max, unit = "m", height = 180, accent = "var(--md-primary)", danger = 60 }) {
    const g = useGrow(true);
    const peak = max || Math.max(10, ...data.map((d) => d.v));
    const [hover, setHover] = useState(null);
    return (
      React.createElement("div", { className: "chart-bars", style: { height } },
        data.map((d, i) => {
          const h = d.v <= 0 ? 0 : Math.max(3, (d.v / peak) * 100 * g);
          const over = d.v > danger;
          return React.createElement("div", {
            key: i, className: "chart-bars__col",
            onMouseEnter: () => setHover(i), onMouseLeave: () => setHover(null),
          },
            hover === i && d.v > 0 && React.createElement("div", { className: "chart-tip" }, d.v + unit),
            React.createElement("div", { className: "chart-bars__track" },
              React.createElement("div", {
                className: "chart-bars__fill" + (d.weekend ? " is-weekend" : "") + (over ? " is-over" : ""),
                style: { height: (d.weekend ? 0 : h) + "%", background: over ? "var(--md-tertiary)" : accent },
              }),
            ),
            React.createElement("div", { className: "chart-bars__label" + (d.today ? " is-today" : "") }, d.label),
          );
        }),
      )
    );
  }

  // ---- Donut gauge (used vs remaining) ----
  function DonutGauge({ value, total, label, sublabel, size = 168, stroke = 16, color = "var(--md-primary)", track = "var(--md-surface-container-high)" }) {
    const g = useGrow(true);
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(1, value / total));
    const off = c * (1 - pct * g);
    return (
      React.createElement("div", { className: "donut", style: { width: size, height: size } },
        React.createElement("svg", { width: size, height: size, viewBox: `0 0 ${size} ${size}` },
          React.createElement("circle", { cx: size / 2, cy: size / 2, r, fill: "none", stroke: track, strokeWidth: stroke }),
          React.createElement("circle", {
            cx: size / 2, cy: size / 2, r, fill: "none", stroke: color, strokeWidth: stroke,
            strokeLinecap: "round", strokeDasharray: c, strokeDashoffset: off,
            transform: `rotate(-90 ${size / 2} ${size / 2})`, style: { transition: "stroke .2s" },
          }),
        ),
        React.createElement("div", { className: "donut__center" },
          React.createElement("div", { className: "donut__value mono" }, label),
          sublabel && React.createElement("div", { className: "donut__sub" }, sublabel),
        ),
      )
    );
  }

  // ---- Linear meter ----
  function LinearMeter({ value, total, color = "var(--md-primary)", height = 12, danger }) {
    const g = useGrow(true);
    const pct = Math.max(0, Math.min(1, value / total)) * 100 * g;
    const isDanger = danger != null && value >= danger;
    return (
      React.createElement("div", { className: "meter", style: { height } },
        React.createElement("div", {
          className: "meter__fill", style: { width: pct + "%", background: isDanger ? "var(--md-error)" : color },
        }),
      )
    );
  }

  // ---- Segmented meter (battery style) ----
  function SegmentMeter({ value, total, segments = 30, color = "var(--md-primary)" }) {
    const g = useGrow(true);
    const filled = Math.round((value / total) * segments * g);
    return (
      React.createElement("div", { className: "segbar" },
        Array.from({ length: segments }).map((_, i) =>
          React.createElement("div", {
            key: i, className: "segbar__seg" + (i < filled ? " is-on" : ""),
            style: i < filled ? { background: color } : null,
          }),
        ),
      )
    );
  }

  // ---- Sparkline ----
  function Sparkline({ data, width = 120, height = 36, color = "var(--md-primary)", fill = true }) {
    if (!data.length) return null;
    const max = Math.max(1, ...data), min = Math.min(0, ...data);
    const span = max - min || 1;
    const pts = data.map((v, i) => [(i / (data.length - 1)) * width, height - ((v - min) / span) * height]);
    const d = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
    const area = d + ` L${width} ${height} L0 ${height} Z`;
    return (
      React.createElement("svg", { className: "spark", width, height, viewBox: `0 0 ${width} ${height}`, preserveAspectRatio: "none" },
        fill && React.createElement("path", { d: area, fill: color, opacity: 0.12 }),
        React.createElement("path", { d, fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }),
      )
    );
  }

  // ---- Stacked horizontal bars (fund vs work-hour split per day) ----
  function StackBars({ rows, max }) {
    const g = useGrow(true);
    const peak = max || Math.max(10, ...rows.map((r) => (r.daily || 0) + r.fund + r.work + r.free + (r.offset || 0)));
    return (
      React.createElement("div", { className: "stack" },
        rows.map((r, i) =>
          React.createElement("div", { key: i, className: "stack__row" },
            React.createElement("div", { className: "stack__label" }, r.label),
            React.createElement("div", { className: "stack__track" },
              r.daily > 0 && React.createElement("div", { className: "stack__seg", style: { width: (r.daily / peak) * 100 * g + "%", background: "var(--ded-daily)" }, title: r.dailyReason ? ("Daily grant — " + r.dailyReason) : "Daily grant" }),
              r.free > 0 && React.createElement("div", { className: "stack__seg", style: { width: (r.free / peak) * 100 * g + "%", background: "var(--ded-free)" }, title: "Free (≤60m permission)" }),
              r.fund > 0 && React.createElement("div", { className: "stack__seg", style: { width: (r.fund / peak) * 100 * g + "%", background: "var(--ded-fund)" }, title: "From fund" }),
              r.offset > 0 && React.createElement("div", { className: "stack__seg", style: { width: (r.offset / peak) * 100 * g + "%", background: "var(--ded-offset)" }, title: "Covered by annual-leave offset" }),
              r.work > 0 && React.createElement("div", { className: "stack__seg", style: { width: (r.work / peak) * 100 * g + "%", background: "var(--ded-unpaid)" }, title: "Unpaid (work hours)" }),
            ),
            React.createElement("div", { className: "stack__total mono" }, ((r.daily || 0) + r.free + r.fund + (r.offset || 0) + r.work) + "m"),
          ),
        ),
      )
    );
  }

  Object.assign(window, { BarChart, DonutGauge, LinearMeter, SegmentMeter, Sparkline, StackBars });
})();
